package tools

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.neverEqualPolicy
import androidx.compose.runtime.setValue
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.toArgb
import androidx.compose.ui.text.input.TextFieldValue
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlin.math.roundToInt
import kotlin.math.roundToLong
import kotlin.reflect.KMutableProperty0
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

sealed interface EField {
    val change: Boolean
    fun applyChanges()
}

sealed class EFieldRegular<I>(
    private val property: KMutableProperty0<I>,
    protected val condition: (I) -> I? = { it },
    val propertyGetter: EFieldRegular<I>.() -> I = { property.get() },
    val propertySetter: EFieldRegular<I>.(newWal: I) -> Unit = { property.set(it) },
    val onChange: (I) -> Unit = {}
) : EField {

    override var change by mutableStateOf(false)
        protected set

    private var origin = propertyGetter()

    val fieldName get() = property.name

    var state by mutableStateOf(propertyGetter())
        private set

    var err by mutableStateOf(false)
        private set

    override fun applyChanges() {
        change = false
        origin = state
    }

    fun set(newValue: I) {
        condition(newValue)?.also {
            propertySetter(it)
            propertyGetter().also { new ->
                change = origin != new
                state = new
            }
            onChange(state)
            onSetInternal(state)
        } ?: kotlin.run {
            err = true
        }
    }

    protected open fun onSetInternal(newValue: I) = Unit

    override fun toString(): String = propertyGetter().toString()
}


class EFieldDualBooleanAsInt(
    property: KMutableProperty0<Int>,
    condition: (Boolean) -> Boolean? = { it },
    propertyGetter: EFieldDual<Int, Boolean>.() -> Boolean = { property.get().toBoolean() },
    propertySetter: EFieldDual<Int, Boolean>.(newWal: Boolean) -> Unit = { property.set(it.toInt()) },
    onChange: (Boolean) -> Unit = {},
) : EFieldDual<Int, Boolean>(property, condition, propertyGetter, propertySetter, onChange = onChange)

class EFieldDualIntAsFloat(
    property: KMutableProperty0<Int>,
    condition: (Float) -> Float? = { it.takeIf { it in 0F..1F } },
    propertyGetter: EFieldDual<Int, Float>.() -> Float = { (property.get().toFloat() / 100F).coerceIn(0F, 1F) },
    propertySetter: EFieldDual<Int, Float>.(newWal: Float) -> Unit = { property.set((it * 100F).roundToInt().coerceIn(0, 100)) },
    onChange: (Float) -> Unit = {},
) : EFieldDual<Int, Float>(property, condition, propertyGetter, propertySetter, onChange = onChange)

class EFieldDualLongAsFloat(
    property: KMutableProperty0<Long>,
    val maxValue: Long,
    condition: (Float) -> Float? = { it.takeIf { it in 0F..1F } },
    propertyGetter: EFieldDual<Long, Float>.() -> Float = {
        (property.get().toFloat() / maxValue.toFloat()).coerceIn(
            0F,
            1F
        )
    },
    propertySetter: EFieldDual<Long, Float>.(newWal: Float) -> Unit = {
        property.set(
            (it * maxValue.toFloat()).roundToLong().coerceIn(0, maxValue)
        )
    },
    onChange: (Float) -> Unit = {},
) : EFieldDual<Long, Float>(property, condition, propertyGetter, propertySetter, onChange = onChange)

class EFieldColorAsLong(
    property: KMutableProperty0<Long>,
    condition: (Color) -> Color? = { it },
    propertyGetter: EFieldDual<Long, Color>.() -> Color = { Color(property.get()) },
    propertySetter: EFieldDual<Long, Color>.(newWal: Color) -> Unit = { property.set(it.toArgb().toLong()) },
    onChange: (Color) -> Unit = {},
    fromText: (String) -> Color? = { it.toHexColor() },
    toText: (Color) -> String? = { it.toHex() },
    onErr: () -> Unit = {},
) : EFieldDual<Long, Color>(property, condition, propertyGetter, propertySetter, fromText, toText, onChange, onErr)

fun Color.toHex() = buildString {
    append("#")
    append((alpha * 255F).roundToInt().toString(16).padStart(2, '0'))
    append((red * 255F).roundToInt().toString(16).padStart(2, '0'))
    append((green * 255F).roundToInt().toString(16).padStart(2, '0'))
    append((blue * 255F).roundToInt().toString(16).padStart(2, '0'))
}.uppercase()


class EFieldMaskInt(
    property: KMutableProperty0<Int>,
    mask: Int,
    condition: (Boolean) -> Boolean? = { it },
    propertyGetter: EFieldDual<Int, Boolean>.() -> Boolean = { property.get() and mask > 0 },
    propertySetter: EFieldDual<Int, Boolean>.(newWal: Boolean) -> Unit = { entry ->
        if (entry) {
            if (property.get() and mask <= 0) {
                property.set(property.get() + mask)
            }
        } else {
            if (property.get() and mask > 0) {
                property.set(property.get() - mask)
            }
        }
    },
    onChange: (Boolean) -> Unit = {},
    fromText: (String) -> Boolean? = { null },
    toText: (Boolean) -> String? = { null },
    onErr: () -> Unit = {},
) : EFieldDual<Int, Boolean>(property, condition, propertyGetter, propertySetter, fromText, toText, onChange, onErr)

class EFieldRegularGeneric<T>(
    property: KMutableProperty0<T>,
    condition: (T) -> T? = { it },
    propertyGetter: EFieldRegular<T>.() -> T = { property.get() },
    propertySetter: EFieldRegular<T>.(newWal: T) -> Unit = { property.set(it) },
    onChange: (T) -> Unit = {},
) : EFieldRegular<T>(property, condition, propertyGetter, propertySetter, onChange)

class EFieldRegularString(
    property: KMutableProperty0<String>,
    condition: (String) -> String? = { it },
    propertyGetter: EFieldRegular<String>.() -> String = { property.get() },
    propertySetter: EFieldRegular<String>.(newWal: String) -> Unit = { property.set(it) },
    onChange: (String) -> Unit = {},
) : EFieldRegular<String>(property, condition, propertyGetter, propertySetter, onChange)

class EFieldRegularDate(
    property: KMutableProperty0<LocalDate>,
    condition: (LocalDate) -> LocalDate? = { it },
    propertyGetter: EFieldRegular<LocalDate>.() -> LocalDate = { property.get() },
    propertySetter: EFieldRegular<LocalDate>.(newWal: LocalDate) -> Unit = { property.set(it) },
    onChange: (LocalDate) -> Unit = {},
) : EFieldRegular<LocalDate>(property, condition, propertyGetter, propertySetter, onChange)

class EFieldRegularDateTime(
    property: KMutableProperty0<LocalDateTime>,
    condition: (LocalDateTime) -> LocalDateTime? = { it },
    propertyGetter: EFieldRegular<LocalDateTime>.() -> LocalDateTime = { property.get() },
    propertySetter: EFieldRegular<LocalDateTime>.(newWal: LocalDateTime) -> Unit = { property.set(it) },
    onChange: (LocalDateTime) -> Unit = {},
) : EFieldRegular<LocalDateTime>(property, condition, propertyGetter, propertySetter, onChange)

class EFieldRegularTime(
    property: KMutableProperty0<LocalTime>,
    condition: (LocalTime) -> LocalTime? = { it },
    propertyGetter: EFieldRegular<LocalTime>.() -> LocalTime = { property.get() },
    propertySetter: EFieldRegular<LocalTime>.(newWal: LocalTime) -> Unit = { property.set(it) },
    onChange: (LocalTime) -> Unit = {},
) : EFieldRegular<LocalTime>(property, condition, propertyGetter, propertySetter, onChange)

class EFieldRegularInt(
    property: KMutableProperty0<Int>,
    condition: (Int) -> Int? = { it },
    propertyGetter: EFieldRegular<Int>.() -> Int = { property.get() },
    propertySetter: EFieldRegular<Int>.(newWal: Int) -> Unit = { property.set(it) },
    onChange: (Int) -> Unit = {},
) : EFieldRegular<Int>(property, condition, propertyGetter, propertySetter, onChange)


fun Int?.toBoolean(): Boolean = this == 1
private fun Boolean?.toInt(): Int = if (this == true) 1 else 0

class EFieldRegularFloat(
    property: KMutableProperty0<Float>,
    condition: (Float) -> Float? = { it },
    propertyGetter: EFieldRegular<Float>.() -> Float = { property.get() },
    propertySetter: EFieldRegular<Float>.(newWal: Float) -> Unit = { property.set(it) },
    onChange: (Float) -> Unit = {},
) : EFieldRegular<Float>(property, condition, propertyGetter, propertySetter, onChange)

class EFieldRegularBoolean(
    property: KMutableProperty0<Boolean>,
    condition: (Boolean) -> Boolean? = { it },
    propertyGetter: EFieldRegular<Boolean>.() -> Boolean = { property.get() },
    propertySetter: EFieldRegular<Boolean>.(newWal: Boolean) -> Unit = { property.set(it) },
    onChange: (Boolean) -> Unit = {},
) : EFieldRegular<Boolean>(property, condition, propertyGetter, propertySetter, onChange)


class EFieldRegularOffset(
    property: KMutableProperty0<Offset>,
    condition: (Offset) -> Offset? = { it },
    propertyGetter: EFieldRegular<Offset>.() -> Offset = { property.get() },
    propertySetter: EFieldRegular<Offset>.(newWal: Offset) -> Unit = { property.set(it) },
    onChange: (Offset) -> Unit = {},
) : EFieldRegular<Offset>(property, condition, propertyGetter, propertySetter, onChange)

class EFieldRegularLong(
    property: KMutableProperty0<Long>,
    condition: (Long) -> Long? = { it },
    propertyGetter: EFieldRegular<Long>.() -> Long = { property.get() },
    propertySetter: EFieldRegular<Long>.(newWal: Long) -> Unit = { property.set(it) },
    onChange: (Long) -> Unit = {},
) : EFieldRegular<Long>(property, condition, propertyGetter, propertySetter, onChange)

sealed class EFieldDual<F, I>(
    private val property: KMutableProperty0<F>,
    protected val condition: (I) -> I? = { it },
    val propertyGetter: EFieldDual<F, I>.() -> I,
    val propertySetter: EFieldDual<F, I>.(newWal: I) -> Unit,
    val fromText: (String) -> I? = { null },
    val toText: (I) -> String? = { null },
    val onChange: (I) -> Unit = {},
    val onErr: () -> Unit = {}
) : EField {

    var state by mutableStateOf(propertyGetter())
        private set

    var err by mutableStateOf(false)
        private set

    var text by mutableStateOf(TextFieldValue(toText(state) ?: ""))
        private set

    override var change by mutableStateOf(false)
        protected set

    private var origin = propertyGetter()

    override fun applyChanges() {
        change = false
        origin = state
    }

    fun setText(newValue: TextFieldValue) {
        text = newValue
        fromText(newValue.text)?.also { it ->
            condition(it)?.also {
                err = false
                propertySetter(it)
                propertyGetter().also { new ->
                    change = origin != new
                    state = new
                }
                onChange(state)
            }
        } ?: run {
            err = true
            onErr()
        }
    }

    fun set(newValue: I) {
        condition(newValue)?.also {
            err = false
            propertySetter(it)
            propertyGetter().also { new ->
                change = origin != new
                state = new
            }
            toText(it)?.also { newText ->
                text = TextFieldValue(newText)
            }
            onChange(state)
        } ?: kotlin.run {
            err = true
            onErr()
        }
    }

    override fun toString(): String = propertyGetter().toString()
}

sealed class EFieldText<I>(
    private val property: KMutableProperty0<I>,
    protected val condition: (String) -> I?,
    val propertyGetter: EFieldText<I>.() -> I = { property.get() },
    val propertySetter: EFieldText<I>.(newWal: I) -> Unit = { property.set(it) },
    val onChange: (I) -> Unit = {},
    val onErr: () -> Unit
) : EField {

    var state by mutableStateOf(propertyGetter())
        private set

    var text by mutableStateOf(TextFieldValue(state.toString()))
        private set

    var err by mutableStateOf(condition(text.text) == null)
        private set

    override var change by mutableStateOf(false)
        protected set

    private var origin = propertyGetter()

    override fun applyChanges() {
        change = false
        origin = state
    }

    fun set(newValue: TextFieldValue) {
        condition(newValue.text)?.also {
            err = false
            propertySetter(it)
            propertyGetter().also { new ->
                change = new != origin
                state = new
            }
            onChange(state)
            onSetInternal(state)

        } ?: kotlin.run {
            err = true
            onErr()
        }
        text = newValue

    }


    protected open fun onSetInternal(newValue: I) = Unit

    override fun toString(): String = propertyGetter().toString()
}

class EFieldTextValue(
    property: KMutableProperty0<String>,
    condition: (String) -> String? = { it },
    propertyGetter: EFieldText<String>.() -> String = { property.get() },
    propertySetter: EFieldText<String>.(newWal: String) -> Unit = { property.set(it) },
    onChange: (String) -> Unit = {},
    onErr: () -> Unit = {}
) : EFieldText<String>(property, condition, propertyGetter, propertySetter, onChange, onErr)

class EFieldTextString(
    property: KMutableProperty0<String>,
    condition: (String) -> String? = { it },
    propertyGetter: EFieldText<String>.() -> String = { property.get() },
    propertySetter: EFieldText<String>.(newWal: String) -> Unit = { property.set(it) },
    onChange: (String) -> Unit = {},
    onErr: () -> Unit = {}
) : EFieldText<String>(property, condition, propertyGetter, propertySetter, onChange, onErr)

class EFieldTextInt(
    property: KMutableProperty0<Int>,
    condition: (String) -> Int? = { it.trim().toIntOrNull() },
    propertyGetter: EFieldText<Int>.() -> Int = { property.get() },
    propertySetter: EFieldText<Int>.(newWal: Int) -> Unit = { property.set(it) },
    onChange: (Int) -> Unit = {},
    onErr: () -> Unit = {}
) : EFieldText<Int>(property, condition, propertyGetter, propertySetter, onChange, onErr)

class EFieldTextFloat(
    property: KMutableProperty0<Float>,
    condition: (String) -> Float? = { it.trim().replace(",", ".").toFloatOrNull() },
    propertyGetter: EFieldText<Float>.() -> Float = { property.get() },
    propertySetter: EFieldText<Float>.(newWal: Float) -> Unit = { property.set(it) },
    onChange: (Float) -> Unit = {},
    onErr: () -> Unit = {}
) : EFieldText<Float>(property, condition, propertyGetter, propertySetter, onChange, onErr)

class EFieldTextDouble(
    property: KMutableProperty0<Double>,
    condition: (String) -> Double? = { it.trim().replace(",", ".").toDoubleOrNull() },
    propertyGetter: EFieldText<Double>.() -> Double = { property.get() },
    propertySetter: EFieldText<Double>.(newWal: Double) -> Unit = { property.set(it) },
    onChange: (Double) -> Unit = {},
    onErr: () -> Unit = {}
) : EFieldText<Double>(property, condition, propertyGetter, propertySetter, onChange, onErr)

open class EFieldTextLong(
    property: KMutableProperty0<Long>,
    condition: (String) -> Long? = { it.trim().toLongOrNull() },
    propertyGetter: EFieldText<Long>.() -> Long = { property.get() },
    propertySetter: EFieldText<Long>.(newWal: Long) -> Unit = { property.set(it) },
    onChange: (Long) -> Unit = {},
    onErr: () -> Unit = {}
) : EFieldText<Long>(property, condition, propertyGetter, propertySetter, onChange, onErr)

class EFieldTextMsAsSec(
    property: KMutableProperty0<Long>,
    condition: (String) -> Long? = { it.trim().toLongOrNull() },
    propertyGetter: EFieldText<Long>.() -> Long = { property.get().milliseconds.inWholeSeconds },
    propertySetter: EFieldText<Long>.(newWal: Long) -> Unit = { property.set(it.seconds.inWholeMilliseconds) },
    onChange: (Long) -> Unit = {},
    onErr: () -> Unit = {}
) : EFieldTextLong(property, condition, propertyGetter, propertySetter, onChange, onErr)

private fun String.toHexColor(): Color? {
    var value = this.trim()
    if (value.startsWith("#")) value = value.substring(1).trim()

    value = when (value.length) {
        3 -> "FF${value[0]}${value[0]}${value[1]}${value[1]}${value[2]}${value[2]}"
        6 -> "FF$value"
        8 -> value
        else -> return null
    }
    return value.toLongOrNull(16)
        ?.let { Color(it) }
}


open class EFieldList<I, O : EFieldList.Entry<I>>(
    protected val ref: () -> MutableList<I>,
    protected val onBind: EFieldList<I, O>.(Int, I) -> O,
    protected val onChange: EFieldList<I, O>.(MutableList<O>) -> Unit = {},
) : EField {

    fun swapOrder(stc: Int, dest: Int) {
        runCatching {
            state.add(dest, state.removeAt(stc))
            ref().add(dest, ref().removeAt(stc))
            state.forEachIndexed { index, o ->
                o.onPositionChanged(index)
            }
            invokeChange()
        }.onFailure { it.printStackTrace() }
    }

    constructor(
        items: MutableList<I>,
        onBindIndexedEntry: EFieldList<I, O>.(Int, I) -> O,
        onChange: EFieldList<I, O>.(MutableList<O>) -> Unit = {}
    ) : this(
        ref = { items },
        onBind = onBindIndexedEntry,
        onChange = onChange
    )

    constructor(
        items: MutableList<I>,
        onBindEntry: EFieldList<I, O>.(I) -> O,
        onChange: EFieldList<I, O>.(MutableList<O>) -> Unit = {}
    ) : this(
        ref = { items },
        onBind = { _, it -> onBindEntry(it) },
        onChange = onChange
    )

    var state by mutableStateOf(
        value = ref().mapIndexed { index, it -> onBind(index, it) }.toMutableList(),
        policy = neverEqualPolicy()
    )
        private set

//    fun rebind(){
//        state =  ref().map { onBindEntry(it) }.toMutableList()
//    }

    fun add(newItem: O) {
        add(state.size, newItem)

    }

//    fun addAll(newItems: List<O>) {
//        newItems.forEach {
//            add(state.size, it)
//        }
//    }

    fun addAll(newItems: List<I>) {
        newItems.forEach {
            add(state.size, it)
        }
    }

    fun add(index: Int, newItem: O): O {
        state.add(index, newItem)
        ref().add(index, newItem.data)
        invokeChange()
        return newItem
    }

    fun add(newItem: I): O {
        return add(state.size, newItem)
    }

    fun add(index: Int, newItem: I): O {
        val newEntry = onBind(index, newItem)
        state.add(index, newEntry)
        ref().add(index, newItem)
        invokeChange()
        return newEntry
    }

    fun updateItem(old: O, new: I): O {
        val index = state.indexOfFirst { it.key == old.key }
        if (index == -1) error("can not update item, because is not in array")

        removeAt(index)
        return add(index, new)
    }

    fun updateItem(old: O, new: O): O {
        val index = state.indexOfFirst { it.key == old.key }
        if (index == -1) error("can not update item, because is not in array")

        removeAt(index)
        return add(index, new)
    }


    private fun invokeChange() {
        state = state
        onChange(state)
    }

    fun removeAt(index: Int) {
        state.removeAt(index)
        ref().removeAt(index)
        invokeChange()
    }

    fun removeAll(condition: (O) -> Boolean) {
        state.filter(condition)
            .forEach {
                val index = state.indexOf(it)
                state.removeAt(index)
                ref().removeAt(index)
            }
        invokeChange()
    }

    override val change: Boolean
        get() = error("not implemented")

    override fun applyChanges() {
        error("not implemented")
    }

    override fun toString(): String = ref().toString()

    open fun release() {
        state = mutableListOf()
        ref().clear()
    }

    interface Entry<I> {
        val data: I
        val key: String
        fun onPositionChanged(newPosition: Int) {}
    }
}
